import { useEffect, useState } from 'react'
import type { RefObject } from 'react'

import { cabinetApi } from '../api'
import type { MapCanvasHandle } from './MapCanvas'
import { defaultTrackRange, periodFromMeta, type TrackMeta } from './vesselOps'

/**
 * Трек одного судна на карте: запрос, слой, состояние кнопки.
 *
 * `mmsi === null` — трека нет: слой снимается. Смена судна ведёт себя так же,
 * как показ нового: старый слой перерисовывается новыми данными (ключ один —
 * трек на карте всегда один, два разноцветных клубка не прочесть).
 *
 * Окно по умолчанию — месяц, кончающийся ПРОСМАТРИВАЕМЫМ днём. Если за него
 * разметки нет, а у судна она есть, окно один раз подменяется на её период
 * (см. periodFromMeta — он же и защищает от петли запросов).
 */
export function useVesselTrack(
  mapRef: RefObject<MapCanvasHandle | null>,
  mmsi: string | null,
  date: string,
) {
  const [range, setRange] = useState(() => defaultTrackRange(date))
  const [meta, setMeta] = useState<TrackMeta | null>(null)
  const [busy, setBusy] = useState(false)

  // Новое судно — новое окно: период, подставленный по прошлому судну, к этому
  // отношения не имеет.
  useEffect(() => {
    if (!mmsi) return
    setRange(defaultTrackRange(date))
    setMeta(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mmsi])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!mmsi) {
      map.removeVesselTrack('vessel')
      setMeta(null)
      return
    }
    let alive = true
    setBusy(true)
    cabinetApi
      .vesselTrack(mmsi, range.from, range.to)
      .then(fc => {
        if (!alive) return
        map.setVesselTrack('vessel', fc)
        setMeta(fc.meta)
        const next = periodFromMeta(fc.meta, range)
        if (next) setRange(next)
      })
      .catch(() => {
        // Молчать нельзя: пустая карта читалась бы как «судно не ходило».
        if (alive) setMeta({ reason: 'error', count: 0 })
      })
      .finally(() => {
        if (alive) setBusy(false)
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mmsi, range.from, range.to])

  // Уход со страницы — слой за собой убираем: он переживает размонтирование
  // карты не лучше любого другого.
  useEffect(
    () => () => {
      mapRef.current?.removeVesselTrack('vessel')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return { busy, meta, range }
}
